"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface Message {
    id: string;
    text: string;
    sender: "user" | "ai";
    timestamp: Date;
    error?: boolean;
}

export default function Chat({ onClose }: { onClose?: () => void }) {
    const [messages, setMessages] = useState<Message[]>([]);
    const [conversationId, setConversationId] = useState<string | null>(null);
    const [inputValue, setInputValue] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Remove or modify the auto-focus behavior for mobile
    useEffect(() => {
        // Only auto-focus on desktop devices
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

        if (!isTyping && inputRef.current && !isMobile) {
            inputRef.current.focus();
        }
    }, [isTyping]);

    // Load initial message from API
    useEffect(() => {
        const loadInitialMessage = async () => {
            try {
                const response = await fetch("/api/assistant", {
                    method: "GET",
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();

                if (data.messages && data.messages.length > 0) {
                    const initialMessages = data.messages.map((msg: any, index: number) => ({
                        id: (index + 1).toString(),
                        text: msg.content,
                        sender: msg.role === "assistant" ? "ai" : "user",
                        timestamp: new Date(data.timestamp),
                    })) as Message[];

                    setMessages(initialMessages);
                }
            } catch (error) {
                console.error("Error loading initial message:", error);
                // Fallback to default message if API fails
                setMessages([
                    {
                        id: "1",
                        text: "Hi there! How can I assist you today?\nHallo! Wie kann ich Ihnen heute helfen?",
                        sender: "ai",
                        timestamp: new Date(),
                    },
                ]);
            } finally {
                setIsLoading(false);
            }
        };

        loadInitialMessage();
    }, []);

    const handleSendMessage = async () => {
        if (inputValue.trim() === "" || isTyping) {
            return;
        } // Prevent sending while AI is typing

        const newMessage: Message = {
            id: Date.now().toString(),
            text: inputValue,
            sender: "user",
            timestamp: new Date(),
        };

        setMessages((prev) => [...prev, newMessage]);
        const currentInput = inputValue;
        setInputValue("");

        // Show typing indicator
        setIsTyping(true);

        try {
            const response = await fetch("/api/assistant", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    message: currentInput,
                    ...(conversationId !== null ? { conversationId } : {}),
                }),
                // Without this the typing indicator can spin forever.
                signal: AbortSignal.timeout(70_000),
            });

            // Read the body first: the server puts a German, user-facing
            // message in `error`, and throwing on status alone would lose it.
            const data = await response.json().catch(() => ({}));

            if (!response.ok || data.error) {
                throw new Error(data.error ?? `HTTP error! status: ${response.status}`);
            }

            setConversationId(data.conversationId);

            const supportMessage: Message = {
                id: (Date.now() + 1).toString(),
                text: data.response,
                sender: "ai",
                timestamp: new Date(),
            };

            setMessages((prev) => [...prev, supportMessage]);
        } catch (error) {
            console.error("Error calling assistant API:", error);

            const isTimeout = error instanceof Error && (error.name === "TimeoutError" || error.name === "AbortError");
            const serverMessage =
                error instanceof Error && error.message && !error.message.startsWith("HTTP error!")
                    ? error.message
                    : null;

            const errorMessage: Message = {
                id: (Date.now() + 1).toString(),
                text: isTimeout
                    ? "Günter braucht gerade zu lange für eine Antwort.\nBitte versuche es erneut!"
                    : (serverMessage ?? "Bei Günter ist ein Fehler aufgetreten.\nBitte versuche es später erneut!"),
                sender: "ai",
                timestamp: new Date(),
                error: true,
            };

            setMessages((prev) => [...prev, errorMessage]);
        } finally {
            setIsTyping(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (!isTyping) {
                // Only send if AI is not typing
                handleSendMessage();
            }
        }
    };

    return (
        <div className="flex flex-col h-full w-full">
            {/* Header */}
            <div className="bg-white border-b border-gray-100 p-4 rounded-t-2xl flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-yellow-400 rounded-full flex items-center justify-center">
                        <Image
                            src="/beeassistant.png"
                            alt="Bee Assistant"
                            width={32}
                            height={32}
                            className="object-contain"
                        />
                    </div>
                    <div>
                        <h3 className="font-medium text-gray-900">Assistant Günter</h3>
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-green-400 rounded-full" />
                            <span className="text-xs text-gray-500">Online</span>
                        </div>
                    </div>
                </div>
                {onClose && (
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100 cursor-pointer"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                )}
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {isLoading ? (
                    <div className="flex justify-center items-center h-full">
                        <div className="text-gray-500 text-sm">Loading...</div>
                    </div>
                ) : (
                    messages.map((message) => (
                        <div
                            key={message.id}
                            className={`flex ${message.sender === "user" ? "justify-end" : "justify-start"}`}
                        >
                            <div className={`flex flex-col ${message.sender === "user" ? "items-end" : "items-start"}`}>
                                <div
                                    className={`max-w-xs md:max-w-xl px-4 py-2 rounded-2xl text-sm ${
                                        message.sender === "user"
                                            ? "bg-yellow-400 text-white"
                                            : message.error
                                              ? "bg-red-100 text-red-800"
                                              : "bg-gray-100 text-gray-800"
                                    }`}
                                >
                                    <div className="prose prose-sm max-w-none">
                                        <ReactMarkdown
                                            remarkPlugins={[remarkGfm]}
                                            components={{
                                                p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                                                code: ({ children, className }) => {
                                                    const isInline = !className;
                                                    return isInline ? (
                                                        <code className="bg-gray-200 text-gray-800 px-1 py-0.5 rounded text-xs font-mono">
                                                            {children}
                                                        </code>
                                                    ) : (
                                                        <pre className="bg-gray-200 text-gray-800 p-2 rounded text-xs font-mono overflow-x-auto">
                                                            <code>{children}</code>
                                                        </pre>
                                                    );
                                                },
                                                strong: ({ children }) => (
                                                    <strong className="font-semibold">{children}</strong>
                                                ),
                                                em: ({ children }) => <em className="italic">{children}</em>,
                                                ul: ({ children }) => (
                                                    <ul className="list-disc pl-4 mb-2">{children}</ul>
                                                ),
                                                ol: ({ children }) => (
                                                    <ol className="list-decimal pl-4 mb-2">{children}</ol>
                                                ),
                                                li: ({ children }) => <li className="mb-1">{children}</li>,
                                                h1: ({ children }) => (
                                                    <h1 className="text-lg font-bold mb-2">{children}</h1>
                                                ),
                                                h2: ({ children }) => (
                                                    <h2 className="text-base font-bold mb-2">{children}</h2>
                                                ),
                                                h3: ({ children }) => (
                                                    <h3 className="text-sm font-bold mb-1">{children}</h3>
                                                ),
                                                blockquote: ({ children }) => (
                                                    <blockquote className="border-l-2 border-gray-300 pl-2 italic text-gray-600 mb-2">
                                                        {children}
                                                    </blockquote>
                                                ),
                                                a: ({ children, href }) => (
                                                    <a
                                                        href={href}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-blue-600 underline hover:text-blue-800"
                                                    >
                                                        {children}
                                                    </a>
                                                ),
                                                table: ({ children }) => (
                                                    <div className="overflow-x-auto mb-2">
                                                        <table className="min-w-full text-xs border-collapse border border-gray-300">
                                                            {children}
                                                        </table>
                                                    </div>
                                                ),
                                                thead: ({ children }) => (
                                                    <thead className="bg-gray-100">{children}</thead>
                                                ),
                                                tbody: ({ children }) => <tbody>{children}</tbody>,
                                                tr: ({ children }) => (
                                                    <tr className="border-b border-gray-200">{children}</tr>
                                                ),
                                                th: ({ children }) => (
                                                    <th className="border border-gray-300 px-2 py-1 text-left font-semibold bg-gray-50">
                                                        {children}
                                                    </th>
                                                ),
                                                td: ({ children }) => (
                                                    <td className="border border-gray-300 px-2 py-1">{children}</td>
                                                ),
                                            }}
                                        >
                                            {message.text}
                                        </ReactMarkdown>
                                    </div>
                                </div>
                                <span className="text-xs text-gray-400 mt-1 px-2">
                                    {typeof message.timestamp === "string"
                                        ? message.timestamp
                                        : `${message.timestamp.getHours().toString().padStart(2, "0")}:${message.timestamp.getMinutes().toString().padStart(2, "0")}`}
                                </span>
                            </div>
                        </div>
                    ))
                )}

                {/* Typing indicator */}
                {isTyping && (
                    <div className="flex justify-start">
                        <div className="bg-gray-100 px-4 py-2 rounded-2xl">
                            <div className="flex space-x-1">
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                                <div
                                    className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                                    style={{ animationDelay: "0.1s" }}
                                />
                                <div
                                    className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                                    style={{ animationDelay: "0.2s" }}
                                />
                            </div>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-gray-100">
                <div className="flex gap-3 items-center">
                    <input
                        ref={inputRef}
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder={isLoading ? "Loading..." : isTyping ? "AI is typing..." : "Type a message..."}
                        disabled={isLoading}
                        className={`flex-1 px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-base resize-none text-gray-900 placeholder-gray-500 ${
                            isTyping || isLoading ? "bg-gray-50" : "bg-white"
                        }`}
                        style={{ fontSize: "16px" }} // Prevent zoom on iOS
                    />
                    <button
                        type="button"
                        onClick={handleSendMessage}
                        disabled={inputValue.trim() === "" || isTyping || isLoading}
                        className="bg-yellow-400 hover:bg-yellow-500 disabled:bg-gray-300 disabled:cursor-not-allowed text-white p-3 rounded-2xl transition-all duration-200"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                            />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
}
